import type { MeetingFull } from '@/redux/internal-services/workspaceApiExtensions';

export type CalView = 'month' | 'week' | 'day';

export function startOfDay(d: Date) {
  const c = new Date(d); c.setHours(0, 0, 0, 0); return c;
}
export function endOfDay(d: Date) {
  const c = new Date(d); c.setHours(23, 59, 59, 999); return c;
}
export function addDays(d: Date, n: number) {
  const c = new Date(d); c.setDate(c.getDate() + n); return c;
}
export function addMonths(d: Date, n: number) {
  const c = new Date(d); c.setMonth(c.getMonth() + n); return c;
}
export function isSameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
/** YYYY-MM-DD in the browser's local timezone — use for day-bucketing, never toISOString(). */
export function dateKey(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
/** UTC ISO string — only for API query params (from_date / to_date). */
export function toISO(d: Date) { return d.toISOString(); }

/** Week range containing `anchor` (Mon–Sun). */
export function weekRange(anchor: Date) {
  const dow = (anchor.getDay() + 6) % 7; // 0=Mon
  const mon = startOfDay(addDays(anchor, -dow));
  return { from: mon, to: endOfDay(addDays(mon, 6)), days: Array.from({ length: 7 }, (_, i) => addDays(mon, i)) };
}

/** 6-week grid for month view. */
export function monthGrid(anchor: Date): Date[][] {
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const startDow = (first.getDay() + 6) % 7;
  const gridStart = addDays(first, -startDow);
  const weeks: Date[][] = [];
  for (let w = 0; w < 6; w++) {
    const week: Date[] = [];
    for (let d = 0; d < 7; d++) week.push(addDays(gridStart, w * 7 + d));
    weeks.push(week);
  }
  return weeks;
}

export interface PositionedMeeting {
  meeting: MeetingFull;
  col: number;
  cols: number;
  top: number;    // px from top of grid
  height: number; // px
}

const HOUR_PX = 64; // px per hour
const DAY_START = 6; // grid starts at 6am

export { HOUR_PX, DAY_START };

export function minutesToPx(minutes: number) { return (minutes / 60) * HOUR_PX; }
export function timeToPx(dt: Date) {
  return minutesToPx((dt.getHours() - DAY_START) * 60 + dt.getMinutes());
}

/** Layout meetings into non-overlapping columns. */
export function layoutMeetings(meetings: MeetingFull[]): PositionedMeeting[] {
  const sorted = [...meetings].sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime());
  const groups: MeetingFull[][] = [];

  for (const m of sorted) {
    const start = new Date(m.start_time).getTime();
    let placed = false;
    for (const g of groups) {
      const lastEnd = Math.max(...g.map((x) => new Date(x.end_time).getTime()));
      if (start >= lastEnd) { g.push(m); placed = true; break; }
    }
    if (!placed) groups.push([m]);
  }

  const cols = groups.length;
  return groups.flatMap((g, ci) =>
    g.map((m) => {
      const start = new Date(m.start_time);
      const end   = new Date(m.end_time);
      const top    = Math.max(0, timeToPx(start));
      const height = Math.max(minutesToPx(30), timeToPx(end) - top);
      return { meeting: m, col: ci, cols, top, height };
    }),
  );
}

export const MEETING_COLORS: Record<string, { bg: string; border: string; text: string }> = {
  site_visit:     { bg: 'bg-teal-500',    border: 'border-teal-600',    text: 'text-white' },
  client_meeting: { bg: 'bg-brand-dark',  border: 'border-brand-dark',  text: 'text-white' },
  internal:       { bg: 'bg-slate-500',   border: 'border-slate-600',   text: 'text-white' },
  follow_up:      { bg: 'bg-purple-500',  border: 'border-purple-600',  text: 'text-white' },
  inspection:     { bg: 'bg-amber-500',   border: 'border-amber-600',   text: 'text-white' },
  estimate:       { bg: 'bg-green-500',   border: 'border-green-600',   text: 'text-white' },
  other:          { bg: 'bg-gray-400',    border: 'border-gray-500',    text: 'text-white' },
};

export const PRIORITY_COLOR: Record<string, string> = {
  low: 'bg-gray-400', normal: 'bg-blue-500', high: 'bg-amber-500', urgent: 'bg-red-500',
};

export function fmtTime(d: Date) {
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}
